import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  lookbookApi,
  type Lookbook,
  type LookbookVisibilityInput,
} from "@/api/lookbook-api";
import { raiseBoardApi } from "@/api/raise-board-api";
import { useWorkspaceRole } from "@/hooks/use-workspace-role";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { useDealsListStore } from "@/store/deals-list-store";
import { useLookbookVisibilityStore } from "@/store/lookbook-visibility-store";
import { getErrorMessage } from "@/lib/api-error";

/**
 * The boards a pipeline deal can be filed onto: the workspace's public
 * Raiseboard, plus every lookbook (a private, link-shared subset of it).
 *
 * This lives next to the pipeline rather than the Raiseboard page because
 * that is where deals are actually triaged — the Raiseboard only ever sees
 * deals that have already been published to it, so filing them from there
 * meant publishing first and organising in a second pass.
 */
export function useAddToBoard(onDone?: () => void) {
  const { workspace, activeWorkspaceId } = useWorkspaceSettings();
  const { isWorkspaceOwner } = useWorkspaceRole();
  const queryClient = useQueryClient();
  const refreshDeals = useDealsListStore((s) => s.refreshDeals);
  const deals = useDealsListStore((s) => s.deals);
  const openVisibilityDialog = useLookbookVisibilityStore((s) => s.open);
  const closeVisibilityDialog = useLookbookVisibilityStore((s) => s.close);

  // Members never see the lookbook half of this menu. They can *read* the
  // workspace's lookbooks (the Lookbooks page and the Raiseboard column show
  // them), but every row here is an action — filing a deal into a lookbook, or
  // creating one from a selection — and both endpoints are gated on a "company
  // manager" affiliation, so they would only fail on submit. The Raiseboard
  // rows stay: publishing to it is not owner-only.
  const lookbooksEnabled =
    workspace?.lookbooks_enabled === true && isWorkspaceOwner;
  const lookbooksKey = ["lookbooks", activeWorkspaceId] as const;

  const { data: lookbooks = [] } = useQuery({
    queryKey: lookbooksKey,
    queryFn: () => lookbookApi.list(activeWorkspaceId as string),
    enabled: !!activeWorkspaceId && lookbooksEnabled,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: lookbooksKey });
    if (activeWorkspaceId) await refreshDeals(activeWorkspaceId);
  };

  const raiseBoardMutation = useMutation({
    mutationFn: async ({
      companyIds,
      member,
    }: {
      companyIds: string[];
      member: boolean;
    }) => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      return raiseBoardApi.bulkSet(activeWorkspaceId, companyIds, member);
    },
    onSuccess: async (_result, { companyIds, member }) => {
      await refresh();
      toast.success(
        member
          ? `Added ${countLabel(companyIds.length)} to the Raiseboard`
          : `Removed ${countLabel(companyIds.length)} from the Raiseboard`
      );
      onDone?.();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update the Raiseboard"));
    },
  });

  const lookbookMutation = useMutation({
    mutationFn: async ({
      lookbookId,
      companyIds,
      member,
      visibility,
    }: {
      lookbookId: string;
      companyIds: string[];
      member: boolean;
      name: string;
      visibility?: LookbookVisibilityInput;
    }) => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      // A lookbook only ever holds deals that are on the Raiseboard —
      // set_lookbook_deals resolves company ids against applications with
      // raise_board:true and 400s when none match. Publishing first is what
      // makes this work from the pipeline, where most deals have never been
      // near the board.
      if (member) {
        await raiseBoardApi.bulkSet(activeWorkspaceId, companyIds, true);
      }
      return lookbookApi.setDeals(
        activeWorkspaceId,
        lookbookId,
        companyIds,
        member,
        visibility
      );
    },
    onSuccess: async (_result, { member, name }) => {
      closeVisibilityDialog();
      await refresh();
      toast.success(member ? `Added to ${name}` : `Removed from ${name}`, {
        description: member
          ? "Also published to your Raiseboard, which lookbooks draw from."
          : undefined,
      });
      onDone?.();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update lookbook"));
    },
  });

  const createLookbookMutation = useMutation({
    mutationFn: async ({
      companyIds,
      name,
      visibility,
    }: {
      companyIds: string[];
      name: string;
      visibility?: LookbookVisibilityInput;
    }) => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      const lookbook = await lookbookApi.create(activeWorkspaceId, name);
      await raiseBoardApi.bulkSet(activeWorkspaceId, companyIds, true);
      await lookbookApi.setDeals(
        activeWorkspaceId,
        lookbook._id,
        companyIds,
        true,
        visibility
      );
      return lookbook;
    },
    onSuccess: async (lookbook) => {
      closeVisibilityDialog();
      await refresh();
      toast.success(`Created ${lookbook.name} and added the selection`);
      onDone?.();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create lookbook"));
    },
  });

  const isPending =
    raiseBoardMutation.isPending ||
    lookbookMutation.isPending ||
    createLookbookMutation.isPending;

  /**
   * Membership reads as checked only when *every* deal in the selection is
   * already there — a partial selection shows unchecked, and ticking it adds
   * the stragglers rather than toggling the whole set off.
   */
  const allOnRaiseBoard = (companyIds: string[]) =>
    companyIds.length > 0 &&
    companyIds.every((id) => deals[id]?.raiseBoard === true);

  const allInLookbook = (lookbook: Lookbook, companyIds: string[]) => {
    const members = new Set(lookbook.company_ids);
    return companyIds.length > 0 && companyIds.every((id) => members.has(id));
  };

  return {
    lookbooksEnabled,
    lookbooks,
    isPending,
    allOnRaiseBoard,
    allInLookbook,
    setRaiseBoard: (companyIds: string[], member: boolean) =>
      raiseBoardMutation.mutate({ companyIds, member }),
    /**
     * Adding opens the visibility dialog instead of mutating: what a lookbook
     * shows for a deal is chosen when the deal is filed, not afterwards.
     * Removing still goes straight through — it takes the whole entry, config
     * and all, so there is nothing to configure.
     */
    setLookbook: (
      lookbook: Lookbook,
      companyIds: string[],
      member: boolean
    ) => {
      if (!member) {
        lookbookMutation.mutate({
          lookbookId: lookbook._id,
          name: lookbook.name,
          companyIds,
          member: false,
        });
        return;
      }

      openVisibilityDialog({
        mode: "existing",
        lookbookId: lookbook._id,
        lookbookName: lookbook.name,
        companyIds,
        onDone,
      });
    },

    /**
     * Creating a lookbook from the pipeline *is* "publish these deals like
     * this", so it opens the same dialog rather than quietly producing a
     * profile-only lookbook that then has to be reconfigured.
     */
    createLookbookWith: (companyIds: string[]) =>
      openVisibilityDialog({
        mode: "create",
        lookbookName: `Lookbook ${lookbooks.length + 1}`,
        companyIds,
        onDone,
      }),

    /** Called by the dialog host once the user confirms. */
    commitLookbook: (args: {
      lookbookId: string;
      name: string;
      companyIds: string[];
      visibility: LookbookVisibilityInput;
    }) => lookbookMutation.mutate({ ...args, member: true }),

    commitNewLookbook: (args: {
      name: string;
      companyIds: string[];
      visibility: LookbookVisibilityInput;
    }) => createLookbookMutation.mutate(args),
  };
}

function countLabel(count: number): string {
  return `${count} deal${count === 1 ? "" : "s"}`;
}
